/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import React, { useEffect } from "react";

import Grid from "@mui/material/Grid";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";

import { toCamelCase } from "./utils";

const DrawerList = ({
  feeLists,
  feeData,
  handleVisibiltyChange,
  setNewFee,
  newFee,
  handleAddNewFee,
  handleCloseDrawer,
  handleApply,
  capitalizeFirstLetterOfEachWord
}) => {
  return (
    <Grid container>
      <List
        style={{
          border: "1px solid #DBE2FF",
          paddingBottom: "0px",
          marginBottom: "16px"
        }}
      >
        <ListItem
          sx={{ borderBottom: "1px solid #DBE2FF", background: "#F8F9FD" }}
        >
          <Typography>Field Name</Typography>
        </ListItem>
        {feeLists?.map((feeConfig, idx) => {
          return (
            <ListItem
              key={idx}
              item
              xs={12}
              sx={{ borderBottom: "1px solid #DBE2FF" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "14px",
                  alignItems: "center"
                }}
              >
                <Checkbox
                  checked={Boolean(
                    feeConfig?.isVisible() && feeData?.[feeConfig?.label]
                  )}
                  onChange={() => {
                    return handleVisibiltyChange(feeConfig.label);
                  }}
                />
                <span>
                  {feeConfig?.label} {feeConfig?.isCurrency ? "($)" : ""}
                </span>
              </div>
            </ListItem>
          );
        })}
      </List>
      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "white",
          padding: "8px",
          border: "1px solid #DBE2FF"
        }}
      >
        <Grid container spacing={2} sx={{ marginBottom: "12px" }}>
          <Grid item xs={8}>
            <TextField
              data-testid='new-fee-drawer'
              size='small'
              onChange={(e) => {
                return setNewFee(e.target.value);
              }}
              placeholder='Type Here'
              value={capitalizeFirstLetterOfEachWord(newFee)}
            />
          </Grid>
          <Grid item xs={4}>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={() => {
                return handleAddNewFee(newFee);
              }}
            >
              Add Fee
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Button
              variant='outlined'
              color='primary'
              size='medium'
              onClick={() => {
                return handleCloseDrawer();
              }}
            >
              Close
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              data-testid='apply-fee-drawer'
              variant='contained'
              color='primary'
              size='medium'
              onClick={handleApply}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};

const TermSheetFeeDrawer = ({
  open,
  closeFeeDrawer,
  feeDetails,
  handleAddNewFeeConfig,
  orgLevelFees,
  capitalizeFirstLetterOfEachWord
}) => {
  const [newFee, setNewFee] = React.useState("");
  const [addedFees, setAddedFees] = React.useState([""]);
  const [feeLists, setFeeLists] = React.useState([]);
  const [feeData, setFeeData] = React.useState([]);
  const [newlyAddedFees, setNewlyAddedFees] = React.useState([]);
  const [visibilityChangedFields, setVisiblityChangedFields] = React.useState(
    []
  );

  useEffect(() => {
    setFeeData(feeDetails);
    setFeeLists(orgLevelFees);
  }, [orgLevelFees, feeDetails]);

  const handleAddNewFee = () => {
    const trimmedFees = newFee?.trim();
    if (
      !feeLists.find((fee) => {
        return fee.label === trimmedFees;
      })
    ) {
      setAddedFees([...addedFees, trimmedFees]);
      setFeeLists([
        ...feeLists,
        {
          id: toCamelCase(trimmedFees),
          label: trimmedFees,
          isVisible: () => {
            return true;
          },
          isEditable: () => {
            return true;
          },
          isCurrency: true
        }
      ]);
      setNewlyAddedFees([...newlyAddedFees, trimmedFees]);
      setFeeData((previous) => {
        return { ...previous, [trimmedFees]: "0.00" };
      });
      setNewFee("");
    }
  };

  const removeDuplicates = (arr) => {
    const uniqueIds = new Set();
    return arr?.reverse()?.filter((obj) => {
      if (uniqueIds.has(obj.id)) {
        return false; // Skip duplicates
      }
      uniqueIds.add(obj.id); // Add unique id
      return true; // Keep unique object
    });
  };

  const handleVisibiltyChange = (label) => {
    const tempFeeLists = feeLists.map((fee) => {
      if (fee.label === label) {
        const isVisible = !feeData[label] ? fee.isVisible() : !fee.isVisible();
        const returnObj = {
          ...fee,
          isVisible: () => {
            return isVisible;
          }
        };
        setVisiblityChangedFields(
          removeDuplicates([...visibilityChangedFields, returnObj])
        );
        return returnObj;
      }
      return fee;
    });
    setFeeLists(tempFeeLists);
    if (!feeData[label]) {
      const newFeeData = {
        ...feeData,
        [label]: "0.00"
      };
      setFeeData({ ...newFeeData });
    }
  };

  const handleCloseDrawer = () => {
    closeFeeDrawer();
    setNewFee("");
    setAddedFees([""]);
  };

  const handleApply = () => {
    handleAddNewFeeConfig(
      feeLists,
      feeData,
      newlyAddedFees,
      visibilityChangedFields
    );
    handleCloseDrawer();
    setNewlyAddedFees([]);
  };

  return (
    <Drawer
      variant='persistent'
      anchor='right'
      open={open}
      onClose={closeFeeDrawer}
      sx={{
        position: "relative", // Keeps drawer inside the modal
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          position: "absolute", // Positions the drawer within the modal
          top: 0,
          right: 0,
          height: "100%", // Fills the height of the modal
          width: "300px", // Set the width you want for the sidebar
          boxShadow: "0px 0px 10px rgba(0,0,0,0.1)"
        }
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          padding: "2px",
          boxSizing: "border-box" // Ensures padding does not affect the overall width
        }}
        role='presentation'
      >
        {DrawerList({
          feeLists,
          feeData,
          handleVisibiltyChange,
          setNewFee,
          newFee,
          handleAddNewFee,
          handleCloseDrawer,
          handleApply,
          capitalizeFirstLetterOfEachWord
        })}
      </Box>
    </Drawer>
  );
};

export default TermSheetFeeDrawer;
